/**
 * batch-66 inkoop-afwijkingen (FO-497) — de boete-bewaking op één loonstrook (PUUR).
 *
 * De loonstrook-controle draait per run, en dezelfde run wordt vaak meer dan één
 * keer gecontroleerd: elke strook die later binnenkomt (mail, losse upload), elke
 * bevestigde voertuigkoppeling en de knop Herdraai controleren de hele run
 * opnieuw, ook maanden later. De verdeling van de inhoudingsregels over de boetes
 * is daarom INCREMENTEEL: wat een eerdere controle met een regel van deze strook
 * heeft gedaan staat vast, alleen wat er nog vrij is wordt verdeeld. Een tweede
 * controle met dezelfde invoer schrijft niets en meldt niets nieuws.
 *
 * Daarvoor onthoudt de bewaking ELKE regel die zij heeft afgehandeld:
 *
 *  - een regel die bij een boete is geboekt staat in de koppeling (één per boete
 *    en strook), die onthoudt uit welke regels zij bestaat (`BoeteStrookRegel`);
 *  - een regel waarvan NIETS is geboekt maar die als ten onrechte ingehouden is
 *    gemeld (`BoeteVerdeelSpoor`) onthoudt de aanroeper bij de melding, en geeft
 *    hij bij de volgende controle terug.
 *
 * Zonder dat geheugen is bij een herdraai niet te zeggen wat er met een regel is
 * gebeurd: de ene inhouding overschrijft dan de andere, één regel dekt twee
 * boetes, of geld dat is teruggevraagd telt later alsnog mee.
 *
 * De volgorde waarin een regel wordt vergeven (wie het eerst past krijgt haar):
 *
 *  0. Wat al is afgehandeld houdt zijn regel bezet: de regels van de bestaande
 *     koppelingen (ook die van een boete die intussen is heropend of afgeboekt en
 *     ook een handmatige koppeling) en de regels die eerder als ten onrechte
 *     ingehouden zijn gemeld. Zo telt één regel nooit twee keer.
 *  1. Elke openstaande boete zoekt haar eigen regel. EERST de boetes die volgens
 *     hun plan een termijn in deze run hebben (die zijn formeel opgegeven), dan de
 *     overige. Een boete zoekt het hele restbedrag, dan de termijn van deze
 *     periode. Een boete waarvan de eigen regel op deze strook al is gevonden (of
 *     die handmatig is gekoppeld) zoekt niet opnieuw.
 *  2. Vinden twee of meer boetes MET een termijn geen eigen regel, dan telt één
 *     regel die precies de SOM van hun termijnen is; elke boete krijgt haar deel,
 *     samen nooit meer dan er op de regel staat.
 *  3. De ingetrokken opgaven op naam van deze medewerker krijgen wat daarna nog
 *     over is: eerst de opgaven die volgens hun plan een termijn in deze run
 *     hadden, dan de opgaven waarvan de inhouding te laat komt.
 *  4. Heeft de medewerker precies ÉÉN boete op deze run en vond die haar regel
 *     niet, dan telt een los deelbedrag: dan valt er niets verkeerd toe te wijzen.
 *     Als laatste, zodat een regel die precies bij een ingetrokken opgave past
 *     daar terechtkomt (en als zodanig wordt gemeld) in plaats van als deelbedrag.
 *
 * Een inhouding op een ingetrokken opgave telt ALLEEN mee op de boete als die nog
 * op deze medewerker staat EN er geen lopende opgave is: een lopende opgave houdt
 * het hele openstaande bedrag al volgens haar plan in, dus wat er daarnaast op een
 * ingetrokken opgave wordt ingehouden is dubbel en hoort terugbetaald. Staat de
 * boete intussen op een ander, of stond er minder open, dan is het bedrag ook ten
 * onrechte ingehouden: niets koppelen, wel melden.
 */
import {
  BOETE_INHOUDING_TOLERANTIE_EUR,
  zoekPassendeInhouding,
  type InhoudingKandidaat,
} from './loonstrook-controle'

export type BoeteStrookRegelSoort = 'OPGAVE' | 'SOM' | 'INGETROKKEN'

/** Eén regel van de loonstrook die (deels) bij een boete is geboekt. */
export interface BoeteStrookRegel {
  /** De plek van de regel in de inhoudingen van de strook (-1 = onbekend: een koppeling van voor deze regel). */
  index: number
  /** Het bedrag van de regel op de strook (euro). */
  bedrag: number
  /** Wat daarvan bij deze boete is geboekt (euro). */
  gekoppeld: number
  /** OPGAVE = de eigen regel van de boete, SOM = één regel voor de termijnen van meerdere boetes, INGETROKKEN = een ingetrokken opgave. */
  soort: BoeteStrookRegelSoort
  /** Bij INGETROKKEN: de sleutel van de ingetrokken opgave (`ingetrokkenOpgaveSleutel`). */
  opgave: string | null
}

/** De sleutel waaronder een overschot op de EIGEN regel van de boete (de lopende opgave) wordt gemeld. */
export const BOETE_LOPENDE_OPGAVE_SLEUTEL = 'OPGAVE'

/** De vaste sleutel van één ingetrokken opgave (de velden wijzigen na het intrekken niet meer). */
export function ingetrokkenOpgaveSleutel(opgave: {
  doorzettingId: string | null
  periode: string
  op: string
}): string {
  return `${opgave.doorzettingId ?? ''}:${opgave.periode}:${opgave.op}`
}

/** Lees de strookregels van een koppeling uit de JSON-kolom (defensief; leeg = onbekende samenstelling). */
export function leesBoeteStrookRegels(json: unknown): BoeteStrookRegel[] {
  if (!Array.isArray(json)) return []
  const getal = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  return json.flatMap((r): BoeteStrookRegel[] => {
    if (!r || typeof r !== 'object' || Array.isArray(r)) return []
    const rij = r as Record<string, unknown>
    const index = getal(rij.index)
    const bedrag = getal(rij.bedrag)
    const gekoppeld = getal(rij.gekoppeld)
    if (index === null || bedrag === null || gekoppeld === null) return []
    // Een onbekende soort telt als de eigen regel: dan zoekt de boete niet opnieuw.
    const soort: BoeteStrookRegelSoort = rij.soort === 'SOM' || rij.soort === 'INGETROKKEN' ? rij.soort : 'OPGAVE'
    return [
      {
        index: Math.trunc(index),
        bedrag,
        gekoppeld,
        soort,
        opgave: typeof rij.opgave === 'string' && rij.opgave ? rij.opgave : null,
      },
    ]
  })
}

/** De JSON-vorm van de strookregels (voor de kolom). */
export function boeteStrookRegelsNaarJson(regels: readonly BoeteStrookRegel[]): Record<string, unknown>[] {
  return regels.map((r) => ({
    index: r.index,
    bedrag: r.bedrag,
    gekoppeld: r.gekoppeld,
    soort: r.soort,
    opgave: r.opgave,
  }))
}

/** Een boete die op deze run verrekend hoort te worden (of door deze run al verrekend is). */
export interface BoeteVerdeelRegulier {
  id: string
  /** Wat er via de loonstrook te verhalen is (euro). */
  bedrag: number
  /** Wat er in totaal op loonstroken is ingehouden (euro), deze strook inbegrepen. */
  verrekend: number
  /** Wat het plan in deze run verwacht (euro), of null zonder termijn in deze run. */
  termijn: number | null
  /** Er ligt een opgave bij de salarisadministratie (de boete is doorgezet). */
  lopendeOpgave: boolean
}

/** Een ingetrokken opgave die op deze strook nog kan terugkomen. */
export interface BoeteVerdeelIngetrokken {
  boeteId: string
  /** `ingetrokkenOpgaveSleutel` van de opgave. */
  opgave: string
  /**
   * De bedragen waarop de opgave in deze run OP TIJD kan terugkomen: zij had
   * volgens haar plan een termijn in deze run (euro, meest waarschijnlijke
   * eerst). Leeg als het plan niets over deze run zegt.
   */
  opTijdBedragen: readonly number[]
  /** De bedragen van een inhouding die TE LAAT komt: wat er voor deze run al had moeten gebeuren (euro). */
  laatBedragen: readonly number[]
  /** Wat er op de boete via de loonstrook te verhalen is (euro). */
  bedrag: number
  /** Wat er op de boete in totaal op loonstroken is ingehouden (euro), deze strook inbegrepen. */
  verrekend: number
  /** De boete staat nog op de medewerker van deze strook. */
  zelfdeBestuurder: boolean
  /** Voor de boete ligt een (nieuwe) opgave bij de salarisadministratie. */
  lopendeOpgave: boolean
}

/** Een levende koppeling op deze strook (van welke boete dan ook). */
export interface BoeteVerdeelRij {
  boeteId: string
  bedrag: number
  /** Door een mens gekoppeld: de automaat raakt deze boete op deze strook niet meer aan. */
  handmatig: boolean
  /** De omschrijving van de regel waarop is gekoppeld (herkomst). */
  omschrijving: string | null
  /** Leeg = onbekende samenstelling (handmatig, of een koppeling van voor deze regel). */
  regels: readonly BoeteStrookRegel[]
}

/**
 * Een regel van deze strook die bij een eerdere controle is afgehandeld ZONDER
 * dat er iets van is gekoppeld: zij is gemeld als ten onrechte ingehouden
 * (terugbetalen). De regel blijft bezet, en wie haar meldde (de boete met haar
 * eigen regel, of een ingetrokken opgave) zoekt op deze strook niet opnieuw.
 */
export interface BoeteVerdeelSpoor {
  boeteId: string
  /** De sleutel waaronder de regel is gemeld: een ingetrokken opgave, of `BOETE_LOPENDE_OPGAVE_SLEUTEL`. */
  opgave: string
  /** De plek van de regel in de inhoudingen van de strook, op het moment van melden. */
  index: number
  /** Het bedrag van de regel op de strook (euro). */
  bedrag: number
}

export interface BoeteVerdeelSchrijf {
  boeteId: string
  /** Het nieuwe totaal van de koppeling op deze strook (euro). */
  bedrag: number
  omschrijving: string | null
  /** De volledige samenstelling na deze controle (bestaand plus nieuw). */
  regels: BoeteStrookRegel[]
}

export interface BoeteVerdeelNaIntrekking {
  boeteId: string
  opgave: string
  /** Wat er van de inhouding bij de boete is geboekt (euro). */
  bedrag: number
  /** In deze controle gevonden (false = al eerder gevonden; de opvolging wordt opnieuw aangeboden). */
  nieuw: boolean
}

/** Waarom een inhouding niet bij de boete is geboekt. */
export type BoeteTenOnrechteReden = 'ANDERE_BESTUURDER' | 'LOPENDE_OPGAVE' | 'MINDER_OPEN'

export interface BoeteVerdeelTenOnrechte {
  boeteId: string
  /** De sleutel van de ingetrokken opgave, of `BOETE_LOPENDE_OPGAVE_SLEUTEL`. */
  opgave: string
  /** Wat er is ingehouden maar NIET bij de boete is geboekt (euro). */
  bedrag: number
  reden: BoeteTenOnrechteReden
  /**
   * De regel van de strook, als er NIETS van is gekoppeld. De aanroeper onthoudt
   * haar bij de melding en geeft haar bij een volgende controle terug als
   * `BoeteVerdeelSpoor`: anders is de regel dan weer vrij. null = de regel is
   * (deels) gekoppeld en staat al in de strookregels van de koppeling.
   */
  regel: { index: number; bedrag: number } | null
}

export interface BoeteVerdeling {
  /** De koppelingen die moeten worden (bij)geschreven. Leeg bij een herdraai zonder nieuwe regels. */
  schrijven: BoeteVerdeelSchrijf[]
  naIntrekking: BoeteVerdeelNaIntrekking[]
  tenOnrechte: BoeteVerdeelTenOnrechte[]
  /** Wat er na deze controle per boete op deze strook staat (euro). */
  opStrook: Map<string, number>
}

const rond = (n: number): number => Math.round(n * 100) / 100
const gelijk = (a: number, b: number): boolean => Math.abs(a - b) < 0.005
const open = (bedrag: number, verrekend: number): number => rond(Math.max(0, bedrag - verrekend))

export function verdeelBoeteInhoudingen(args: {
  kandidaten: readonly InhoudingKandidaat[]
  /** De levende koppelingen op deze strook. */
  rijen: readonly BoeteVerdeelRij[]
  /** In de volgorde waarin zij een regel mogen claimen (oudste eerst). */
  regulier: readonly BoeteVerdeelRegulier[]
  ingetrokken: readonly BoeteVerdeelIngetrokken[]
  /** De regels van deze strook die eerder zonder koppeling zijn gemeld. */
  sporen?: readonly BoeteVerdeelSpoor[]
}): BoeteVerdeling {
  const { kandidaten } = args
  const sporen = args.sporen ?? []
  const rijen = args.rijen.filter((r) => r.bedrag > 0)
  const rijVan = new Map(rijen.map((r) => [r.boeteId, r]))
  const opIndex = new Map(kandidaten.map((k) => [k.index, k]))
  const verbruikt = new Set<number>()

  // ── Stap 0: wat al is afgehandeld houdt zijn regel bezet ──
  // Eerst de regels die op dezelfde plek met hetzelfde bedrag terug te vinden
  // zijn (ook als die plek al bezet is: een som-regel wordt door meerdere boetes
  // gedeeld). Dan de regels die verschoven zijn (de strook is opnieuw uitgelezen):
  // de eerste vrije regel met hetzelfde bedrag. Pas daarna het deel waarvan de
  // regel onbekend is: een hele koppeling (handmatig, of van voor deze regel) of
  // de plaatshouder van zo'n koppeling die later is aangevuld.
  const vast: { index: number; bedrag: number }[] = []
  for (const rij of rijen) {
    for (const r of rij.regels) if (r.index >= 0) vast.push(r)
  }
  for (const s of sporen) if (s.index >= 0) vast.push(s)
  const verschoven: { index: number; bedrag: number }[] = []
  for (const r of vast) {
    const zelfde = opIndex.get(r.index)
    if (zelfde && gelijk(zelfde.bedrag, r.bedrag)) verbruikt.add(zelfde.index)
    else verschoven.push(r)
  }
  for (const r of verschoven) {
    const ander = kandidaten.find((k) => !verbruikt.has(k.index) && gelijk(k.bedrag, r.bedrag))
    if (ander) verbruikt.add(ander.index)
  }
  for (const rij of rijen) {
    const onbekend =
      rij.regels.length === 0
        ? rij.bedrag
        : rij.regels.reduce((som, r) => som + (r.index < 0 ? r.bedrag : 0), 0)
    if (onbekend <= 0) continue
    // De regel met dezelfde omschrijving (bij meerdere de regel waarvan het bedrag
    // het dichtst bij ligt), anders de regel die bij het bedrag past. Een
    // handmatige koppeling kan ook op een regel staan die niet als boete
    // herkenbaar is.
    const tekst = (rij.omschrijving ?? '').trim().toLowerCase()
    let regel: InhoudingKandidaat | null = null
    if (tekst) {
      for (const k of kandidaten) {
        if (verbruikt.has(k.index) || (k.omschrijving ?? '').trim().toLowerCase() !== tekst) continue
        if (!regel || Math.abs(k.bedrag - onbekend) < Math.abs(regel.bedrag - onbekend)) regel = k
      }
    }
    regel ??= zoekPassendeInhouding(onbekend, kandidaten, { verbruikt, alleenBoeteRegels: !rij.handmatig })
    if (regel) verbruikt.add(regel.index)
  }
  /** Wie op deze strook al een regel zonder koppeling heeft gemeld (per boete en sleutel): die zoekt niet opnieuw. */
  const gemeld = new Set(sporen.map((s) => `${s.boeteId}|${s.opgave}`))

  const nieuw = new Map<string, BoeteStrookRegel[]>()
  const eersteOmschrijving = new Map<string, string | null>()
  const nieuwBedrag = (boeteId: string): number =>
    (nieuw.get(boeteId) ?? []).reduce((som, r) => som + r.gekoppeld, 0)
  /** De regel is bezet; wat er bij de boete is geboekt wordt onthouden (niets geboekt = alleen bezet). */
  const claim = (
    boeteId: string,
    regel: InhoudingKandidaat,
    gekoppeld: number,
    soort: BoeteStrookRegelSoort,
    opgave: string | null,
  ): void => {
    verbruikt.add(regel.index)
    if (gekoppeld <= 0) return
    const lijst = nieuw.get(boeteId) ?? []
    lijst.push({ index: regel.index, bedrag: regel.bedrag, gekoppeld: rond(gekoppeld), soort, opgave })
    nieuw.set(boeteId, lijst)
    if (!eersteOmschrijving.has(boeteId)) eersteOmschrijving.set(boeteId, regel.omschrijving || null)
  }
  // De AUTOMATISCHE koppeling boekt alleen op een regel die herkenbaar een boete
  // is. Een bedrag-alleen-treffer ("Kledingbijdrage EUR 139") zou de boete anders
  // stil op verrekend zetten; die koppeling hoort een bewuste handeling te zijn.
  const zoek = (bedrag: number, deel = false): InhoudingKandidaat | null =>
    zoekPassendeInhouding(bedrag, kandidaten, { verbruikt, toestaanDeelbedrag: deel, alleenBoeteRegels: true })

  const naIntrekking: BoeteVerdeelNaIntrekking[] = []
  const tenOnrechte: BoeteVerdeelTenOnrechte[] = []

  /** Wat er op de boete nog openstaat, NA alles wat op deze strook al voor haar is geboekt. */
  const restVan = (boete: BoeteVerdeelRegulier): number => open(boete.bedrag, boete.verrekend + nieuwBedrag(boete.id))
  /**
   * De eigen regel van de boete is gevonden. Nooit meer koppelen dan er nog
   * openstaat: een inhouding die iets hoger uitvalt (tolerantie) mag de boete niet
   * "overbetalen". Het overschot is ten onrechte ingehouden.
   */
  const koppelEigenRegel = (boete: BoeteVerdeelRegulier, match: InhoudingKandidaat): void => {
    const gekoppeld = Math.min(match.bedrag, restVan(boete))
    claim(boete.id, match, gekoppeld, 'OPGAVE', null)
    const teVeel = rond(match.bedrag - gekoppeld)
    if (teVeel > BOETE_INHOUDING_TOLERANTIE_EUR) {
      tenOnrechte.push({
        boeteId: boete.id,
        opgave: BOETE_LOPENDE_OPGAVE_SLEUTEL,
        bedrag: teVeel,
        reden: 'MINDER_OPEN',
        // Niets gekoppeld: de regel staat nergens, de aanroeper onthoudt haar.
        regel: gekoppeld <= 0 ? { index: match.index, bedrag: match.bedrag } : null,
      })
    }
  }
  /**
   * Mag de boete op deze strook (nog) haar eigen regel zoeken? Niet als zij
   * handmatig is gekoppeld, als de samenstelling van haar koppeling onbekend is,
   * of als haar eigen regel op deze strook al is gevonden (geboekt, of gemeld
   * omdat er niets meer openstond).
   */
  const magZoeken = (boete: BoeteVerdeelRegulier): boolean => {
    const rij = rijVan.get(boete.id)
    if (rij && (rij.handmatig || rij.regels.length === 0 || rij.regels.some((r) => r.soort !== 'INGETROKKEN'))) return false
    if (gemeld.has(`${boete.id}|${BOETE_LOPENDE_OPGAVE_SLEUTEL}`)) return false
    return !(nieuw.get(boete.id) ?? []).some((r) => r.soort !== 'INGETROKKEN')
  }

  // ── Stap 1: elke boete haar eigen regel ──
  const zonderRegel: { boete: BoeteVerdeelRegulier; verwacht: number }[] = []
  const metTermijn = args.regulier.filter((b) => b.termijn !== null)
  const zonderTermijn = args.regulier.filter((b) => b.termijn === null)
  for (const boete of [...metTermijn, ...zonderTermijn]) {
    const rij = rijVan.get(boete.id)
    // Een overschot op de eigen regel dat eerder is gevonden: opnieuw aanbieden
    // (de opvolging is idempotent per boete, strook en opgave).
    for (const r of rij?.regels ?? []) {
      const teVeel = rond(r.bedrag - r.gekoppeld)
      if (r.soort === 'OPGAVE' && r.index >= 0 && teVeel > BOETE_INHOUDING_TOLERANTIE_EUR) {
        tenOnrechte.push({
          boeteId: boete.id,
          opgave: BOETE_LOPENDE_OPGAVE_SLEUTEL,
          bedrag: teVeel,
          reden: 'MINDER_OPEN',
          regel: null,
        })
      }
    }
    if (!magZoeken(boete)) continue
    // Zonder lopende opgave kan een ingetrokken opgave van DEZE strook al op de
    // boete zijn geboekt (een eerdere controle van dezelfde run; in een eerste
    // controle komt stap 3 pas na deze stap). Dat telt niet mee in het zoekbedrag,
    // anders zocht een herdraai een ander bedrag dan de eerste controle. Met een
    // lopende opgave boekt stap 3 nooit op de boete, en is het plan berekend op
    // wat er werkelijk openstaat. Een termijn in deze run komt uit een plan, en
    // een plan hoort bij een lopende opgave.
    const lopend = boete.lopendeOpgave || boete.termijn !== null
    const naIntrekkingHier =
      rij && !lopend ? rij.regels.reduce((som, r) => som + (r.soort === 'INGETROKKEN' ? r.gekoppeld : 0), 0) : 0
    const zoekRest = open(boete.bedrag, boete.verrekend - naIntrekkingHier)
    if (zoekRest <= 0) continue
    const verwacht = boete.termijn !== null ? Math.min(boete.termijn, zoekRest) : zoekRest
    let match: InhoudingKandidaat | null = zoek(zoekRest)
    if (!match && verwacht < zoekRest) match = zoek(verwacht)
    if (!match) {
      if (boete.termijn !== null) zonderRegel.push({ boete, verwacht })
      continue
    }
    koppelEigenRegel(boete, match)
  }

  // ── Stap 2: één regel voor de termijnen van meerdere boetes samen ──
  if (zonderRegel.length >= 2) {
    const som = rond(zonderRegel.reduce((s, z) => s + z.verwacht, 0))
    const match = zoek(som)
    if (match) {
      // Samen nooit meer dan er op de regel staat: valt de regel binnen de
      // tolerantie lager uit dan de som, dan krijgt de laatste boete de rest.
      let over = match.bedrag
      for (const z of zonderRegel) {
        const deel = rond(Math.max(0, Math.min(z.verwacht, restVan(z.boete), over)))
        claim(z.boete.id, match, deel, 'SOM', null)
        over = rond(over - deel)
      }
    }
  }

  // ── Stap 3: de ingetrokken opgaven op naam van deze medewerker ──
  // Twee rondes: eerst de opgaven die volgens hun plan een termijn in deze run
  // hadden, dan de opgaven waarvan de inhouding te laat komt. Anders gaat een
  // regel naar een oudere opgave ("een maand te laat") terwijl zij precies de
  // termijn van een latere opgave is.
  const gevonden = new Set<string>()
  for (const laat of [false, true]) {
    for (const item of args.ingetrokken) {
      const sleutel = `${item.boeteId}|${item.opgave}`
      const rij = rijVan.get(item.boeteId)
      if (rij?.handmatig) continue
      const al = rij?.regels.find((r) => r.soort === 'INGETROKKEN' && r.opgave === item.opgave)
      if (al) {
        // Bij een eerdere controle van deze run gevonden en geboekt: niet opnieuw
        // zoeken, wel de opvolging opnieuw aanbieden (één keer per controle; die
        // is idempotent per boete, strook en opgave).
        if (!laat) {
          naIntrekking.push({ boeteId: item.boeteId, opgave: item.opgave, bedrag: al.gekoppeld, nieuw: false })
          const rest = rond(al.bedrag - al.gekoppeld)
          if (rest > BOETE_INHOUDING_TOLERANTIE_EUR) {
            tenOnrechte.push({ boeteId: item.boeteId, opgave: item.opgave, bedrag: rest, reden: 'MINDER_OPEN', regel: null })
          }
        }
        continue
      }
      // Eerder gemeld zonder koppeling (de regel staat al bezet), of in de eerste
      // ronde van deze controle gevonden: één regel per opgave en strook.
      if (gemeld.has(sleutel) || gevonden.has(sleutel)) continue
      let match: InhoudingKandidaat | null = null
      for (const bedrag of laat ? item.laatBedragen : item.opTijdBedragen) {
        match = zoek(bedrag)
        if (match) break
      }
      if (!match) continue
      gevonden.add(sleutel)
      // De inhouding telt alleen mee als de boete nog op deze medewerker staat en er
      // geen lopende opgave is. Wat er op deze strook al voor de boete is gevonden
      // (haar eigen regel, een andere ingetrokken opgave) gaat van de ruimte af.
      const magMeetellen = item.zelfdeBestuurder && !item.lopendeOpgave
      const ruimte = magMeetellen ? open(item.bedrag, item.verrekend + nieuwBedrag(item.boeteId)) : 0
      const opBoete = Math.min(match.bedrag, ruimte)
      claim(item.boeteId, match, opBoete, 'INGETROKKEN', item.opgave)
      if (opBoete > 0) naIntrekking.push({ boeteId: item.boeteId, opgave: item.opgave, bedrag: rond(opBoete), nieuw: true })
      // Wat niet op de boete past is ten onrechte ingehouden. Een verschil binnen
      // de tolerantie van de matcher is een afronding, geen terugbetaling.
      const teVeel = rond(match.bedrag - opBoete)
      if (teVeel > 0 && (opBoete <= 0 || teVeel > BOETE_INHOUDING_TOLERANTIE_EUR)) {
        tenOnrechte.push({
          boeteId: item.boeteId,
          opgave: item.opgave,
          bedrag: teVeel,
          reden: !item.zelfdeBestuurder ? 'ANDERE_BESTUURDER' : item.lopendeOpgave ? 'LOPENDE_OPGAVE' : 'MINDER_OPEN',
          // Niets gekoppeld: de regel staat nergens, de aanroeper onthoudt haar.
          regel: opBoete <= 0 ? { index: match.index, bedrag: match.bedrag } : null,
        })
      }
    }
  }

  // ── Stap 4: het losse deelbedrag van de enige boete op deze run ──
  const enige = args.regulier.length === 1 ? args.regulier[0] : undefined
  if (enige && magZoeken(enige) && restVan(enige) > 0) {
    const match = zoek(restVan(enige), true)
    if (match) koppelEigenRegel(enige, match)
  }

  const opStrook = new Map<string, number>()
  for (const rij of rijen) opStrook.set(rij.boeteId, rij.bedrag)
  const schrijven: BoeteVerdeelSchrijf[] = []
  for (const [boeteId, regels] of nieuw) {
    const rij = rijVan.get(boeteId)
    // Een koppeling van voor deze regel krijgt een plaatshouder voor haar oude
    // deel, zodat dat deel bij een volgende controle zijn regel bezet houdt en de
    // boete niet opnieuw naar haar eigen regel zoekt.
    const bestaand: BoeteStrookRegel[] = rij
      ? rij.regels.length > 0
        ? [...rij.regels]
        : [{ index: -1, bedrag: rij.bedrag, gekoppeld: rij.bedrag, soort: 'OPGAVE', opgave: null }]
      : []
    const bedrag = rond((rij?.bedrag ?? 0) + regels.reduce((som, r) => som + r.gekoppeld, 0))
    schrijven.push({
      boeteId,
      bedrag,
      omschrijving: rij?.omschrijving ?? eersteOmschrijving.get(boeteId) ?? null,
      regels: [...bestaand, ...regels],
    })
    opStrook.set(boeteId, bedrag)
  }
  return { schrijven, naIntrekking, tenOnrechte, opStrook }
}
